import type Handsontable from 'handsontable';
import { textRenderer } from 'handsontable/renderers';

import { MultiSelectEditor } from '@/components/UI/SpreadSheet/editors/MultiSelectEditor';
import { _isNil, _isNotBlank, _notNil } from '@/littledash';
import type {
  DosingMatrixRow,
  DosingMatrixRowKey,
  DosingSessionDraft,
  TreatmentOption,
} from '@/components/Samples/SamplePlanTable/Execute/ExecuteSamplesSetDosingStep.utils';
import {
  defaultSelectableOption,
  isSelectable,
} from '@/components/Samples/SamplePlanTable/Execute/ExecuteSamplesSetDosingStep.utils';
import type { TreatmentApiId } from '@/model/Treatment.model';
import type { CellProperties, CellValue, ColumnSettings, GridSettings } from '@/support/hot.types';

export const TREATMENT_COLUMN = 'treatment';
export const TREATMENT_COLUMN_INDEX = 2;

const ROW_HEIGHT = 44;
/** Handsontable's column header row, which sits outside the `rowHeights` budget. */
const HEADER_HEIGHT = 45;
const BORDER_HEIGHT = 2;
const MAX_GRID_HEIGHT = 420;

export interface DosingSessionGridRow {
  studyGroup: string;
  animalCount: number;
  treatment: string;
  /** Study group colour, absent on the ungrouped row. Drives the swatch the group cell renders. */
  color?: string;
}

/**
 * Per-row cell settings for the treatment column. `optionsMap` is what `MultiSelectEditor` reads to
 * build its picker, letting each row offer only the treatments mapped to its study group.
 */
export interface TreatmentCellSettings {
  optionsMap: Map<string, TreatmentOption>;
  options: Array<string>;
  validator: ReturnType<typeof MultiSelectEditor.validatorFactory>;
}

interface DosingSessionColumnSettings extends ColumnSettings {
  isMultiSelect?: boolean;
  isClearable?: boolean;
}

/**
 * Grid rows for a session: one per matrix row, with the group and its animal count fixed and the
 * treatment pre-filled from the session being edited, or from the row's default choice.
 *
 * The default only applies to a new session: a saved session omits blank rows, so on re-open a row a
 * designer deliberately cleared is indistinguishable from one never set, and re-filling it would undo
 * their choice.
 */
export const buildGridRows = (
  rows: Array<DosingMatrixRow>,
  rowOptions: Map<DosingMatrixRowKey, Array<TreatmentOption>>,
  session?: DosingSessionDraft
): Array<DosingSessionGridRow> =>
  rows.map((row) => {
    const options = rowOptions.get(row.key) ?? [];
    const existing = session?.treatments.get(row.key);
    const chosen = _notNil(existing)
      ? options.find((option) => option.treatmentApiId === existing)
      : _isNil(session)
        ? defaultSelectableOption(options)
        : undefined;
    return {
      studyGroup: row.name,
      animalCount: row.animalCount,
      treatment: chosen?.value ?? '',
      ...(_notNil(row.group?.color) ? { color: row.group.color } : {}),
    };
  });

/**
 * Treatment assignments a designer left in the grid, keyed by matrix row. Rows left blank (and
 * values no longer offered by the row) are omitted, matching the payload's "a study group left
 * unassigned is simply absent".
 */
export const collectGridTreatments = (
  gridRows: Array<DosingSessionGridRow>,
  rows: Array<DosingMatrixRow>,
  rowOptions: Map<DosingMatrixRowKey, Array<TreatmentOption>>
): Map<DosingMatrixRowKey, TreatmentApiId> =>
  rows.reduce<Map<DosingMatrixRowKey, TreatmentApiId>>((acc, row, index) => {
    const value = gridRows[index]?.treatment;
    if (_isNotBlank(value)) {
      const option = (rowOptions.get(row.key) ?? []).find((candidate) => candidate.value === value);
      if (_notNil(option?.treatmentApiId) && option.isDisabled !== true) {
        acc.set(row.key, option.treatmentApiId);
      }
    }
    return acc;
  }, new Map());

/**
 * The picker offers every option so a designer can see the disabled ones exist, but only selectable
 * ones validate — otherwise a pasted volume treatment passes validation and is then silently dropped
 * by `collectGridTreatments`.
 */
export const buildTreatmentCellSettings = (options: Array<TreatmentOption>): TreatmentCellSettings => {
  const selectableValues = options.reduce<Array<string>>((acc, option) => {
    if (isSelectable(option)) {
      acc.push(option.value);
    }
    return acc;
  }, []);
  return {
    optionsMap: new Map(options.map((option) => [option.value, option])),
    options: options.map((option) => option.value),
    validator: MultiSelectEditor.validatorFactory(selectableValues, false, 0, 1),
  };
};

/**
 * Renders the group name behind its colour swatch. Handsontable cells are plain DOM, so the shared
 * React `GroupLabel` cannot be reused here; the swatch is built to match its 14px rounded square.
 */
const studyGroupRenderer = (
  hotInstance: Handsontable.Core,
  td: HTMLTableCellElement,
  row: number,
  column: number,
  prop: string | number,
  value: CellValue,
  cellProperties: CellProperties
) => {
  textRenderer(hotInstance, td, row, column, prop, value, cellProperties);

  const color = (hotInstance.getSourceDataAtRow(row) as DosingSessionGridRow | undefined)?.color;
  if (_isNotBlank(color)) {
    const swatch = document.createElement('span');
    Object.assign(swatch.style, {
      width: '14px',
      height: '14px',
      display: 'inline-block',
      borderRadius: '2px',
      marginRight: '8px',
      verticalAlign: 'middle',
      backgroundColor: color,
    });
    td.prepend(swatch);
  }

  return td;
};

/**
 * The study group and its animal count are context, not input: both columns are read-only so the
 * only thing a designer can change in the grid is the treatment.
 */
export const buildColumns = (): Array<DosingSessionColumnSettings> => [
  { data: 'studyGroup', type: 'text', readOnly: true, className: 'htMiddle', width: 220, renderer: studyGroupRenderer },
  { data: 'animalCount', type: 'numeric', readOnly: true, className: 'htMiddle htLeft', width: 90 },
  {
    data: TREATMENT_COLUMN,
    type: MultiSelectEditor.type,
    isMultiSelect: false,
    isClearable: true,
    className: 'htMiddle',
    width: 280,
  },
];

/**
 * @param sessionName heads the treatment column — a session's column is identified by its own name
 */
export const initDosingSessionSettings = (
  columns: Array<DosingSessionColumnSettings>,
  rowCount: number,
  sessionName: string
): GridSettings => ({
  columns,
  colHeaders: ['Study group', 'Count', sessionName],
  rowHeaders: false,
  columnSorting: false,
  manualColumnResize: true,
  contextMenu: false,
  fillHandle: false,
  outsideClickDeselects: false,
  minSpareRows: 0,
  rowHeights: ROW_HEIGHT,
  height: Math.min(rowCount * ROW_HEIGHT + HEADER_HEIGHT + BORDER_HEIGHT, MAX_GRID_HEIGHT),
  className: 'htMiddle',
  stretchH: 'all' as const,
});
